import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { IUserData } from "@/features/auth/types";
import { useAuthStore } from "@/shared/store/useAuthStore";

type FormState = {
  login: string;
  password: string;
};

export default function SignIn(): React.ReactElement {
  const navigate = useNavigate();
  const { setIsAuth, setUser } = useAuthStore();

  const [form, setForm] = useState<FormState>({ login: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(() => {
    return (
      form.login.trim().length > 0 &&
      form.password.trim().length > 0 &&
      !submitting
    );
  }, [form, submitting]);

  const onChange =
    (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((p) => ({ ...p, [key]: e.target.value }));
      setError("");
    };

  async function demoAuth(payload: { login: string; password: string }) {
    await new Promise((r) => setTimeout(r, 200));

    if (payload.login !== "admin" || payload.password !== "admin") {
      throw new Error("Логин ёки парол нотўғри");
    }

    const user: IUserData = {
      id: 1 as any,
      full_name: "Администратор" as any,
      username: "admin" as any,
      role: "admin" as any,
    };

    return user;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      setSubmitting(true);
      const user = await demoAuth(form);

      setUser(user);
      setIsAuth(true);
      navigate("/", { replace: true });
    } catch (err: any) {
      setUser(null);
      setIsAuth(false);
      setError(err?.message || "Хатолик");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* Title */}
        <div className="mb-6 text-center">
          <img
            src="/src/assets/symbol.png"
            alt="Лого"
            className="mx-auto mb-2 h-16 w-16"
          />
          <div className="text-base font-semibold text-slate-900">
            Ягона миллий лойиҳа бошқарув тизими
          </div>
          <div className="mt-1 text-sm text-slate-600">Тизимга кириш</div>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Login */}
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Логин
            </label>
            <input
              value={form.login}
              onChange={onChange("login")}
              placeholder="Логин"
              className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
            />
          </div>

          {/* Password */}
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Парол
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={onChange("password")}
                placeholder="Парол"
                className="h-10 w-full rounded-md border border-slate-300 px-3 pr-20 text-sm outline-none focus:border-slate-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute top-1/2 right-2 -translate-y-1/2 text-xs text-slate-600"
              >
                {showPassword ? "Яшириш" : "Кўрсатиш"}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-10 w-full rounded-md bg-slate-900 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {submitting ? "Кирилмоқда..." : "Кириш"}
          </button>
        </form>
      </div>
    </div>
  );
}
